<template>
  <v-dialog v-model="visible" max-width="1200px" persistent>
    <v-card class="modern-modal" elevation="24">
      <!-- Header con gradiente -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <v-icon size="28" color="white">mdi-alert-decagram</v-icon>
          </div>
          <div class="header-text">
            <h2 class="modal-title">Registro y Prorrateo de Defectos por Lote</h2>
            <p class="modal-subtitle">Configure los pesos de defectos por lote para su distribución proporcional por línea.</p>
          </div>
        </div>
        <v-btn icon @click="close" class="close-btn" color="white" elevation="0">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="modal-body">
        <!-- Stepper visual inactivo superior -->
        <div class="progress-steps mb-8 mt-2">
          <div class="step completed">
            <div class="step-circle">
              <v-icon color="white" size="16">mdi-check</v-icon>
            </div>
            <span class="step-label">Cultivo y Periodo</span>
          </div>
          <div class="step-divider"></div>
          <div class="step completed">
            <div class="step-circle">
              <v-icon color="white" size="16">mdi-check</v-icon>
            </div>
            <span class="step-label">Configuración</span>
          </div>
          <div class="step-divider"></div>
          <div class="step active">
            <div class="step-circle">
              <span>3</span>
            </div>
            <span class="step-label">Registro de Defectos</span>
          </div>
        </div>

        <!-- Fila de selectores superiores -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <s-select-definition
              :def="1173"
              v-model="form.TypeCultive"
              @input="onCultiveChange"
              outlined
              dense
              hide-details="auto"
              label="Cultivo"
            ></s-select-definition>
          </v-col>
          <v-col cols="12" md="4">
            <s-select
              :items="tunnels"
              item-value="CecID"
              item-text="CecDescription"
              v-model="form.CecID"
              @input="onTunnelChange"
              clearable
              outlined
              dense
              hide-details="auto"
              :disabled="!form.TypeCultive"
              label="Túnel"
            ></s-select>
          </v-col>
          <v-col cols="12" md="4">
            <s-select
              :items="periods"
              item-value="PchID"
              item-text="displayText"
              v-model="form.PchID"
              @input="onPeriodChange"
              clearable
              outlined
              dense
              hide-details="auto"
              :disabled="!form.CecID"
              label="Periodo"
            ></s-select>
          </v-col>
        </v-row>

        <v-row v-if="form.PchID > 0">
          <!-- Columna Izquierda: Ingresar Lote y Defectos -->
          <v-col cols="12" md="6">
            <v-card class="defect-input-card pa-4" elevation="2">
              <h3 class="defects-title mb-4">Ingreso de Lote y Defectos</h3>
              <v-row class="mb-4">
                <v-col cols="12" md="8">
                  <s-select
                    :items="availableLots"
                    item-value="RcfID"
                    item-text="displayText"
                    v-model="selectedLote"
                    return-object
                    clearable
                    outlined
                    dense
                    hide-details="auto"
                    label="Seleccionar Lote"
                  ></s-select>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn
                    color="primary"
                    block
                    class="add-lote-btn"
                    :disabled="!selectedLote || isLotAlreadyAdded"
                    @click="addLotToMemory"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Agregar
                  </v-btn>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <!-- Lista de defectos del cultivo seleccionado -->
              <h4 class="defects-title mb-2">Defectos a Registrar</h4>
              <div v-if="activeDefects.length === 0" class="text-center py-4 grey--text">
                No hay defectos configurados para este cultivo.
              </div>
              <div v-else class="defects-grid" style="max-height: 400px; overflow-y: auto;">
                <div v-for="(defect, idx) in activeDefects" :key="defect.DfpID" class="defect-item py-2 px-3 mb-2">
                  <div class="defect-info">
                    <span class="defect-name font-weight-medium">{{ defect.DfpName || defect.DfpDescription }}</span>
                  </div>
                  <div class="defect-input">
                    <s-text
                      type="decimal"
                      v-model="defectWeights[defect.DfpID]"
                      outlined
                      dense
                      hide-details
                      suffix="kg"
                      class="text-right"
                    ></s-text>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Columna Derecha: Lotes Registrados en Memoria -->
          <v-col cols="12" md="6">
            <v-card class="registered-lotes-card pa-4" elevation="2" style="height: 100%; display: flex; flex-direction: column;">
              <h3 class="defects-title mb-4">Lotes Registrados en este Periodo</h3>
              
              <div v-if="loteDetails.length === 0" class="empty-state my-auto">
                <v-icon size="64" color="grey lighten-2" class="empty-icon">mdi-tray-blank</v-icon>
                <h4 class="empty-title">No hay lotes registrados</h4>
                <p class="empty-description">Seleccione un lote, complete los kilos de defectos y agréguelo a la lista.</p>
              </div>
              <div v-else class="flex-grow-1" style="overflow-y: auto;">
                <v-simple-table class="modern-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Lote</th>
                        <th class="text-center">Juliano</th>
                        <th class="text-right">Peso Total (kg)</th>
                        <th class="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(lote, index) in loteDetails" :key="lote.RcfID">
                        <td class="text-left font-weight-bold">{{ lote.LotCode }}</td>
                        <td class="text-center">{{ lote.Juliano }}</td>
                        <td class="text-right font-weight-bold primary--text">{{ lote.PsgTotalWeight.toFixed(2) }} kg</td>
                        <td class="text-center">
                          <div class="table-actions d-flex justify-center">
                            <v-btn icon color="info" small @click="editLotFromMemory(lote)">
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon color="error" small @click="removeLotFromMemory(lote.RcfID)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <div v-else class="text-center py-12 grey--text">
          <v-icon size="64" color="grey lighten-2" class="mb-4">mdi-filter-variant</v-icon>
          <h3>Seleccione el Cultivo, Túnel y Periodo para iniciar el registro.</h3>
        </div>
      </v-card-text>

      <!-- Footer -->
      <div class="modal-footer">
        <div class="footer-content">
          <v-btn text class="cancel-btn" @click="close">Cancelar</v-btn>
          <v-btn
            class="save-btn"
            :disabled="loteDetails.length === 0 || isSaving"
            :loading="isSaving"
            @click="saveAll"
          >
            Guardar Registro
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import _sProcessSampling from '@/services/FrozenProduction/ProcessSamplingService';
import _sProductionCategory from '@/services/FrozenProduction/ProductionCategory.js';
import _sFrzDefectsProcessService from "@/services/FrozenProduction/FrzDefectsProcessService.js";
import _sTurnService from "@/services/FreshProduction/PrfTurnService";

export default {
  name: 'FrzDefectLoteRegistrationPanel',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    baseParams: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    visible: false,
    isSaving: false,
    tunnels: [],
    periods: [],
    availableLots: [],
    activeDefects: [],
    selectedLote: null,
    defectWeights: {},
    loteDetails: [],
    form: {
      TypeCultive: null,
      CecID: null,
      PchID: null
    },
    activeTurn: null
  }),
  computed: {
    isLotAlreadyAdded() {
      if (!this.selectedLote) return false;
      return this.loteDetails.some(l => l.RcfID === this.selectedLote.RcfID);
    }
  },
  watch: {
    value(val) {
      this.visible = val;
      if (val) {
        this.initialize();
      }
    },
    visible(val) {
      this.$emit('input', val);
    },
    'form.PchID'(val) {
      this.onPeriodChange();
    }
  },
  methods: {
    async initialize() {
      this.loteDetails = [];
      this.resetLotForm();
      
      // Cargar categorías/túneles
      await this.loadTunnels();
      // Obtener turno activo
      await this.loadActiveTurn();

      // Pre-cargar valores si vienen de props
      if (this.baseParams) {
        if (this.baseParams.TypeCultive) {
          this.form.TypeCultive = this.baseParams.TypeCultive;
          await this.onCultiveChange();
        }
        if (this.baseParams.CecID) {
          this.form.CecID = this.baseParams.CecID;
          await this.onTunnelChange();
        }
        if (this.baseParams.PchID) {
          this.form.PchID = this.baseParams.PchID;
          await this.onPeriodChange();
        }
      }
    },
    loadTunnels() {
      return _sProductionCategory.list(this.$fun.getUserID()).then(r => {
        if (r.status === 200) {
          this.tunnels = r.data;
        }
      });
    },
    loadActiveTurn() {
      return _sTurnService.turnActive({ TypeArea: 4 }, this.$fun.getUserID()).then(r => {
        if (r.status === 200 && r.data.length > 0) {
          this.activeTurn = r.data[0];
        }
      });
    },
    onCultiveChange() {
      this.form.CecID = null;
      this.form.PchID = null;
      this.periods = [];
      this.availableLots = [];
      this.activeDefects = [];
      this.loteDetails = [];
      this.resetLotForm();
      
      if (this.form.TypeCultive) {
        // Cargar defectos del cultivo
        _sFrzDefectsProcessService.list({ TypeCultive: this.form.TypeCultive }, this.$fun.getUserID()).then(r => {
          if (r.status === 200) {
            this.activeDefects = r.data;
          }
        });
      }
    },
    onTunnelChange() {
      this.form.PchID = null;
      this.periods = [];
      this.availableLots = [];
      this.loteDetails = [];
      this.resetLotForm();

      if (this.form.CecID && this.form.TypeCultive) {
        // Cargar periodos activos para el túnel y turno
        let obj = {
          ClpID: 0, // 0 para consultar a nivel de túnel consolidado
          CecID: this.form.CecID,
          TurnID: this.activeTurn ? this.activeTurn.TrnID : null
        };
        _sProcessSampling.listPeriod(obj, this.$fun.getUserID()).then(r => {
          if (r.status === 200) {
            this.periods = r.data.filter(x => x.PchState === 2).map(item => ({
              ...item,
              displayText: [item.PchPeriodo, item.TypeCutName, item.CumNombre, item.TypeCropName].filter(Boolean).join(' - ')
            }));
          }
        });
      }
    },
    onPeriodChange() {
      this.availableLots = [];
      this.loteDetails = [];
      this.resetLotForm();

      if (this.form.PchID) {
        // Consultar los lotes disponibles para el periodo seleccionado
        let obj = { PchID: this.form.PchID };
        _sProcessSampling.getLotsByPeriod(obj, this.$fun.getUserID()).then(r => {
          if (r.status === 200) {
            this.availableLots = r.data.map(item => {
              const lotCode = item.CttName || item.LotCode || 'S/L';
              const juliano = item.DtbJuliano || item.Juliano || '';
              const quantity = item.DlpQuantity || item.TotalJabasConsumed || 0;
              return {
                ...item,
                LotCode: lotCode,
                Juliano: juliano,
                displayText: item.RcfDate || `${lotCode} - Juliano ${juliano} (${quantity} Jabas)`
              };
            });
          }
        });
      }
    },
    resetLotForm() {
      this.selectedLote = null;
      this.defectWeights = {};
      this.activeDefects.forEach(d => {
        this.$set(this.defectWeights, d.DfpID, 0);
      });
    },
    addLotToMemory() {
      if (!this.selectedLote) return;

      const rcfId = this.selectedLote.RcfID;
      const defects = [];
      let totalWeight = 0;

      this.activeDefects.forEach(defect => {
        const weight = parseFloat(this.defectWeights[defect.DfpID]) || 0;
        if (weight > 0) {
          defects.push({
            DfpID: defect.DfpID,
            DfpName: defect.DfpName || defect.DfpDescription,
            PsdWeight: weight
          });
          totalWeight += weight;
        }
      });

      if (defects.length === 0) {
        this.$fun.alert("Debe registrar al menos un defecto con peso mayor a 0 kg.", "warning");
        return;
      }

      // Buscar si el lote ya existe en la lista de guardados en memoria
      const existingIdx = this.loteDetails.findIndex(l => l.RcfID === rcfId);
      if (existingIdx > -1) {
        // Reemplazar o actualizar
        this.$set(this.loteDetails, existingIdx, {
          RcfID: rcfId,
          LotCode: this.selectedLote.LotCode,
          Juliano: this.selectedLote.Juliano,
          PsgTotalWeight: totalWeight,
          defects: defects
        });
      } else {
        // Insertar nuevo
        this.loteDetails.push({
          RcfID: rcfId,
          LotCode: this.selectedLote.LotCode,
          Juliano: this.selectedLote.Juliano,
          PsgTotalWeight: totalWeight,
          defects: defects
        });
      }

      this.$fun.alert("Lote agregado temporalmente a la lista.", "success");
      this.resetLotForm();
    },
    editLotFromMemory(lote) {
      // Cargar el lote seleccionado de nuevo al formulario izquierdo para edición rápida
      const matchedLot = this.availableLots.find(l => l.RcfID === lote.RcfID);
      if (matchedLot) {
        this.selectedLote = matchedLot;
      } else {
        this.selectedLote = {
          RcfID: lote.RcfID,
          LotCode: lote.LotCode,
          Juliano: lote.Juliano
        };
      }

      // Limpiar pesos actuales y cargar los del lote en memoria
      this.activeDefects.forEach(d => {
        this.$set(this.defectWeights, d.DfpID, 0);
      });

      lote.defects.forEach(d => {
        this.$set(this.defectWeights, d.DfpID, d.PsdWeight);
      });
    },
    removeLotFromMemory(rcfId) {
      this.loteDetails = this.loteDetails.filter(l => l.RcfID !== rcfId);
      this.$fun.alert("Lote removido de la lista temporal.", "info");
    },
    close() {
      this.visible = false;
    },
    async saveAll() {
      if (this.loteDetails.length === 0) return;

      this.isSaving = true;
      try {
        // Por cada lote en loteDetails, guardaremos proporcionalmente los defectos en el backend
        let errors = [];
        for (const lot of this.loteDetails) {
          const payload = {
            PchID: this.form.PchID,
            RcfID: lot.RcfID,
            Defects: lot.defects.map(d => ({
              DfpID: d.DfpID,
              PsdWeight: d.PsdWeight
            }))
          };

          const r = await _sProcessSampling.saveProportional(payload, this.$fun.getUserID());
          if (r.status !== 200 || !r.data.success) {
            errors.push(r.data.message || `Error al guardar lote ${lot.LotCode}`);
          }
        }

        if (errors.length > 0) {
          this.$fun.alert("Hubo errores al persistir algunos lotes: " + errors.join(" | "), "error");
        } else {
          this.$fun.alert("Todos los defectos fueron registrados y prorrateados con éxito.", "success");
          this.$emit('saved');
          this.close();
        }
      } catch (err) {
        console.error("Error saving proportional defects:", err);
        this.$fun.alert("Error de comunicación al intentar registrar los defectos.", "error");
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.modern-modal {
  border-radius: 16px !important;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #e65100 0%, #ffb74d 100%);
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-content {
  display: flex;
  align-items: center;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 12px;
  margin-right: 16px;
}

.header-text {
  color: white;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}

.modal-subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 4px 0 0 0;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
}

.modal-body {
  padding: 32px;
  background: #f8f9fa;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.step.active {
  opacity: 1;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step.active .step-circle {
  background: #e65100;
  color: white;
}

.step.completed .step-circle {
  background: #4caf50;
  color: white;
}

.step-divider {
  width: 60px;
  height: 2px;
  background: #e0e0e0;
  margin: 0 16px;
}

.step-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
}

.step.active .step-label {
  color: #e65100;
}

.defect-input-card, .registered-lotes-card {
  border-radius: 12px;
  border: 1px solid #ffe0b2;
  background: white;
}

.defects-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #3e2723;
  margin: 0;
}

.add-lote-btn {
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
}

.defects-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.defect-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 12px;
  transition: background-color 0.2s;
}

.defect-item:hover {
  background: #fff3e0;
}

.defect-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.defect-name {
  font-size: 0.95rem;
  color: #5d4037;
}

.defect-input {
  width: 140px;
  margin-left: 12px;
}

.modern-table {
  background: transparent;
}

.table-actions {
  display: flex;
  gap: 4px;
}

.empty-state {
  text-align: center;
  padding: 48px 24px;
}

.empty-icon {
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #5d4037;
  margin-bottom: 8px;
}

.empty-description {
  font-size: 0.9rem;
  color: #7f8c8d;
  line-height: 1.5;
}

.modal-footer {
  background: white;
  border-top: 1px solid #e0e0e0;
  padding: 16px 32px;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.cancel-btn {
  font-weight: 500;
}

.save-btn {
  background: linear-gradient(135deg, #e65100 0%, #f57c00 100%) !important;
  color: white !important;
  font-weight: 500;
  padding: 0 24px;
  height: 44px;
  border-radius: 8px;
}

.save-btn:disabled {
  background: #e0e0e0 !important;
  color: #9e9e9e !important;
}

@media (max-width: 960px) {
  .progress-steps {
    flex-direction: column;
    gap: 16px;
  }
  
  .step-divider {
    width: 2px;
    height: 40px;
    margin: 8px 0;
  }
  
  .modal-body {
    padding: 24px 16px;
  }
}
</style>
